import React, { useState, useEffect } from 'react';
import { useKpros } from '../../context/KprosContext';
import { useAuth } from '../../context/AuthContext';
import { Paperclip } from 'lucide-react';

const clipStyle = document.createElement('style');
clipStyle.textContent = `
  @keyframes kpros-bounce {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    15% { transform: translateY(-8px) rotate(-6deg); }
    30% { transform: translateY(0) rotate(4deg); }
    45% { transform: translateY(-4px) rotate(-3deg); }
    60% { transform: translateY(0) rotate(2deg); }
    75% { transform: translateY(-2px) rotate(-1deg); }
    90% { transform: translateY(0) rotate(0deg); }
  }
  @keyframes kpros-wiggle {
    0%, 100% { transform: rotate(0deg); }
    25% { transform: rotate(-12deg); }
    75% { transform: rotate(12deg); }
  }
  @keyframes kpros-bubble-pop {
    0% { opacity: 0; transform: scale(0.8) translateY(4px); }
    100% { opacity: 1; transform: scale(1) translateY(0); }
  }
  @keyframes kpros-bubble-fade {
    0% { opacity: 1; }
    100% { opacity: 0; transform: translateY(-4px); }
  }
  @keyframes kpros-glow {
    0%, 100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4); }
    50% { box-shadow: 0 0 12px 4px rgba(16, 185, 129, 0.3); }
  }
  .kpros-clippy {
    animation: kpros-bounce 4s ease-in-out infinite, kpros-glow 4s ease-in-out infinite;
    animation-delay: 1s;
  }
  .kpros-clippy:hover {
    animation: kpros-wiggle 0.4s ease-in-out;
    box-shadow: 0 0 14px 5px rgba(16, 185, 129, 0.35);
  }
  .kpros-bubble-show {
    animation: kpros-bubble-pop 0.3s ease-out forwards;
  }
  .kpros-bubble-hide {
    animation: kpros-bubble-fade 0.5s ease-in forwards;
  }
  @media (prefers-reduced-motion: reduce) {
    .kpros-clippy { animation: none; }
    .kpros-clippy:hover { animation: none; }
  }
`;
document.head.appendChild(clipStyle);

const TIPS = [
  "Need help navigating JPRO?",
  "Click me for quick help!",
  "I can help you find anything!",
  "Got a question? I'm here!",
  "Let me guide you around!",
];

export default function KprosFab() {
  const { panelOpen, setPanelOpen } = useKpros();
  const { currentUser } = useAuth();
  const [hidden, setHidden] = useState(() => localStorage.getItem('kpros_hidden') === 'true');
  const [position, setPosition] = useState(() => localStorage.getItem('kpros_position') || 'right');
  const [bubble, setBubble] = useState({ text: '', visible: false, fading: false });

  useEffect(() => {
    const handler = () => {
      setHidden(localStorage.getItem('kpros_hidden') === 'true');
      setPosition(localStorage.getItem('kpros_position') || 'right');
    };
    window.addEventListener('kpros-visibility-changed', handler);
    return () => window.removeEventListener('kpros-visibility-changed', handler);
  }, []);

  // Show a speech bubble periodically
  useEffect(() => {
    if (panelOpen || hidden) return;

    // Show first bubble after 3 seconds
    const initialTimeout = setTimeout(() => {
      showBubble();
    }, 3000);

    // Then show every 30 seconds
    const interval = setInterval(() => {
      showBubble();
    }, 30000);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [panelOpen, hidden]);

  function showBubble() {
    const tip = TIPS[Math.floor(Math.random() * TIPS.length)];
    setBubble({ text: tip, visible: true, fading: false });

    // Start fading after 4 seconds
    setTimeout(() => {
      setBubble(prev => ({ ...prev, fading: true }));
    }, 4000);

    // Hide completely after fade
    setTimeout(() => {
      setBubble({ text: '', visible: false, fading: false });
    }, 4500);
  }

  if (!currentUser?.allowedFeatures?.includes('kpros')) return null;
  if (panelOpen || hidden) return null;

  const isLeft = position === 'left';

  return (
    <div className={`fixed bottom-24 z-50 ${isLeft ? 'left-5' : 'right-5'}`}>
      {/* Speech bubble */}
      {bubble.visible && (
        <div
          className={`absolute bottom-full mb-3 ${isLeft ? 'left-0' : 'right-0'} ${
            bubble.fading ? 'kpros-bubble-hide' : 'kpros-bubble-show'
          }`}
        >
          <div className="relative bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 text-xs font-medium px-3 py-2 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 whitespace-nowrap">
            {bubble.text}
            {/* Speech bubble tail */}
            <div className={`absolute top-full ${isLeft ? 'left-4' : 'right-4'} w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-white dark:border-t-gray-800`} />
          </div>
        </div>
      )}

      {/* Clippy button */}
      <button
        onClick={() => setPanelOpen(true)}
        className="kpros-clippy w-11 h-11 rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 shadow-lg hover:shadow-xl transition-colors duration-200 flex items-center justify-center cursor-pointer"
        title="Open Kpros"
      >
        <Paperclip className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
      </button>
    </div>
  );
}
